import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { COLOR_PALETTE } from './Constants';

const ToggleButton = styled.button`
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: ${props => props.active ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(255, 255, 255, 0.5);
    color: rgba(255, 255, 255, 0.8);
  }
`;

// Simple ambient drone using Web Audio API
const createAmbientSound = (audioContext) => {
  const masterGain = audioContext.createGain();
  masterGain.gain.value = 0.15;
  masterGain.connect(audioContext.destination);

  // Create multiple oscillators for rich drone
  const frequencies = [55, 82.5, 110, 165]; // A1, E2, A2, E3 - open fifths
  const oscillators = [];

  frequencies.forEach((freq, i) => {
    const osc = audioContext.createOscillator();
    const gain = audioContext.createGain();

    osc.type = 'sine';
    osc.frequency.value = freq;

    // Slight detuning for warmth
    osc.detune.value = (Math.random() - 0.5) * 10;

    gain.gain.value = 0.3 / (i + 1); // Lower volumes for higher frequencies

    osc.connect(gain);
    gain.connect(masterGain);

    oscillators.push({ osc, gain });
  });

  // Add subtle LFO for movement
  const lfo = audioContext.createOscillator();
  const lfoGain = audioContext.createGain();
  lfo.type = 'sine';
  lfo.frequency.value = 0.1; // Very slow
  lfoGain.gain.value = 2; // Subtle pitch wobble
  lfo.connect(lfoGain);

  oscillators.forEach(({ osc }) => {
    lfoGain.connect(osc.detune);
  });

  return {
    start: () => {
      oscillators.forEach(({ osc }) => osc.start());
      lfo.start();
    },
    stop: () => {
      oscillators.forEach(({ osc }) => osc.stop());
      lfo.stop();
    },
    setVolume: (v) => {
      masterGain.gain.setTargetAtTime(v * 0.15, audioContext.currentTime, 0.5);
    }
  };
};

export const SoundToggle = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const audioContextRef = useRef(null);
  const ambientRef = useRef(null);

  useEffect(() => {
    // Check localStorage for preference
    const saved = localStorage.getItem('improvta-sound');
    if (saved === 'on') {
      // Don't auto-play, but show as "was on"
    }

    return () => {
      if (ambientRef.current) {
        ambientRef.current.stop();
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  const toggle = () => {
    if (!isInitialized) {
      // First click: initialize audio
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      ambientRef.current = createAmbientSound(audioContextRef.current);
      ambientRef.current.start();
      setIsInitialized(true);
      setIsPlaying(true);
      localStorage.setItem('improvta-sound', 'on');
    } else if (isPlaying) {
      // Fade out
      ambientRef.current.setVolume(0);
      setIsPlaying(false);
      localStorage.setItem('improvta-sound', 'off');
    } else {
      // Fade in
      if (audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume();
      }
      ambientRef.current.setVolume(1);
      setIsPlaying(true);
      localStorage.setItem('improvta-sound', 'on');
    }
  };

  return (
    <ToggleButton
      onClick={toggle}
      active={isPlaying}
      title={isPlaying ? 'Mute ambient sound' : 'Play ambient sound'}
    >
      {isPlaying ? '♫' : '♪'}
    </ToggleButton>
  );
};
